import type { StaticWhitespaceCardProps } from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import type { WhitespaceGridCell as WhitespaceGridDataCell } from 'hooks/useWhitespaceAnalysisGridData';
import { useCallback, useRef } from 'react';
import StaticWhitespaceCard from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_WHITESPACE_CELL_HOVER,
    PPA_WHITESPACE_ENTITY_LIST_COLLAPSED,
    PPA_WHITESPACE_ENTITY_MORE_CLICKED,
} from 'utils/mixpanelEventNames';
import { buildWhitespaceCellId, getWhitespaceCellHoverProps } from './whitespaceMixpanel.helpers';

type WhitespaceGridCell = Partial<StaticWhitespaceCardProps>;

const getYAxisAttributeProps = (isYAxisAttribute: boolean, yAxis: string, rowLabel: string) => ({
    yAxisAttributeLabel: isYAxisAttribute ? yAxis : undefined,
    yAxisAttributeValue: isYAxisAttribute ? rowLabel : undefined,
});

type UseWhitespaceCellRendererParams = {
    yAxis: string;
    crossSection: string;
    showWhitespaces: boolean;
    selectedYAxisKpiId: string;
    isYAxisAttribute?: boolean;
    getCellPaginationData: (params: {
        bucketLabel: string;
        columnHeader: string;
        defaultItems: StaticWhitespaceCardProps['items'];
        defaultMoreCount: number;
    }) => {
        items: StaticWhitespaceCardProps['items'];
        moreCount: number;
        isLoading: boolean;
        showCollapse: boolean;
    };
    collapseCellItems: (params: { bucketLabel: string; columnHeader: string }) => void;
    loadMoreForCell: (params: {
        bucketLabel: string;
        columnHeader: string;
        defaultItems: StaticWhitespaceCardProps['items'];
        defaultMoreCount: number;
    }) => void;
};

const useWhitespaceCellRenderer = ({
    yAxis,
    crossSection,
    showWhitespaces,
    selectedYAxisKpiId,
    isYAxisAttribute = false,
    getCellPaginationData,
    collapseCellItems,
    loadMoreForCell,
}: UseWhitespaceCellRendererParams) => {
    const hoveredCellsRef = useRef<Set<string>>(new Set());

    const handleCellHover = useCallback(
        (cellId: string, gridCell: WhitespaceGridDataCell, rowLabel: string, columnHeader: string) => {
            if (hoveredCellsRef.current.has(cellId)) return;
            hoveredCellsRef.current.add(cellId);
            trackEventMixpanel(PPA_WHITESPACE_CELL_HOVER, getWhitespaceCellHoverProps(gridCell, rowLabel, columnHeader, yAxis));
        },
        [yAxis],
    );

    const handleCollapseClick = useCallback(
        (cellId: string, rowLabel: string, columnHeader: string, entityCount: number) => {
            collapseCellItems({ bucketLabel: rowLabel, columnHeader });
            trackEventMixpanel(PPA_WHITESPACE_ENTITY_LIST_COLLAPSED, {
                cell_id: cellId,
                cross_section: crossSection,
                total_entity_count: entityCount,
            });
        },
        [collapseCellItems, crossSection],
    );

    const handleMoreClick = useCallback(
        (cellId: string, cell: WhitespaceGridCell, rowLabel: string, columnHeader: string, visibleCount: number, hiddenCount: number) => {
            trackEventMixpanel(PPA_WHITESPACE_ENTITY_MORE_CLICKED, {
                cell_id: cellId,
                cross_section: crossSection,
                hidden_entity_count: hiddenCount,
                visible_entity_count: visibleCount,
                sort_metric: selectedYAxisKpiId,
            });
            loadMoreForCell({
                bucketLabel: rowLabel,
                columnHeader,
                defaultItems: cell.items ?? [],
                defaultMoreCount: cell.moreCount ?? 0,
            });
        },
        [crossSection, loadMoreForCell, selectedYAxisKpiId],
    );

    const renderWhitespaceCell = useCallback(
        (cell: WhitespaceGridCell, rowLabel: string, columnHeader: string) => {
            const paginationData = getCellPaginationData({
                bucketLabel: rowLabel,
                columnHeader,
                defaultItems: cell.items ?? [],
                defaultMoreCount: cell.moreCount ?? 0,
            });
            const cellId = buildWhitespaceCellId(columnHeader, yAxis, rowLabel);
            const gridCell = cell as WhitespaceGridDataCell;
            const { yAxisAttributeLabel, yAxisAttributeValue } = getYAxisAttributeProps(
                isYAxisAttribute,
                yAxis,
                rowLabel,
            );

            return (
                <StaticWhitespaceCard
                    valueSales={cell.valueSales ?? ''}
                    valuePrefix={cell.valuePrefix ?? ''}
                    valueSuffix={cell.valueSuffix ?? ''}
                    trendValue={typeof cell.trendValue === 'number' ? cell.trendValue : null}
                    trendDirection={cell.trendDirection ?? 'neutral'}
                    avgValueSales={cell.avgValueSales ?? ''}
                    avgPrefix={cell.avgPrefix ?? ''}
                    avgSuffix={cell.avgSuffix ?? ''}
                    ownShareKpi={cell.ownShareKpi ?? null}
                    crossSectionShareLabel={cell.crossSectionShareLabel ?? ''}
                    crossSectionLabel={crossSection}
                    items={paginationData.items}
                    moreCount={paginationData.moreCount}
                    heatmapColor={cell.heatmapColor ?? 'transparent'}
                    isWhitespace={Boolean(cell.isWhitespace)}
                    showWhitespaces={showWhitespaces}
                    isMoreLoading={paginationData.isLoading}
                    showCollapseButton={paginationData.showCollapse}
                    selectedYAxisKpiId={selectedYAxisKpiId}
                    yAxisAttributeLabel={yAxisAttributeLabel}
                    yAxisAttributeValue={yAxisAttributeValue}
                    onCellHover={() => handleCellHover(cellId, gridCell, rowLabel, columnHeader)}
                    onCollapseClick={() =>
                        handleCollapseClick(
                            cellId,
                            rowLabel,
                            columnHeader,
                            paginationData.items.length + paginationData.moreCount,
                        )
                    }
                    onMoreClick={() =>
                        handleMoreClick(
                            cellId,
                            cell,
                            rowLabel,
                            columnHeader,
                            paginationData.items.length,
                            paginationData.moreCount,
                        )
                    }
                />
            );
        },
        [
            crossSection,
            getCellPaginationData,
            handleCellHover,
            handleCollapseClick,
            handleMoreClick,
            isYAxisAttribute,
            selectedYAxisKpiId,
            showWhitespaces,
            yAxis,
        ],
    );

    return renderWhitespaceCell;
};

export default useWhitespaceCellRenderer;
